import { useEffect, useState } from 'react';
import restaurantService, { CustomerDisplaySettings } from '../services/restaurantService';
import { DEFAULT_ITEM_NOTE_MAX, DEFAULT_ORDER_NOTE_MAX } from '../utils/menuOptions';

/** Outlet rules for guest-written kitchen notes (design D10). */
export interface GuestNoteSettings {
  itemNotesEnabled: boolean;
  orderNoteEnabled: boolean;
  presets: string[];
  itemNoteMaxLength: number;
  orderNoteMaxLength: number;
}

/** Notes stay hidden until the outlet settings are known. */
export const NOTES_UNKNOWN: GuestNoteSettings = {
  itemNotesEnabled: false,
  orderNoteEnabled: false,
  presets: [],
  itemNoteMaxLength: DEFAULT_ITEM_NOTE_MAX,
  orderNoteMaxLength: DEFAULT_ORDER_NOTE_MAX,
};

export function noteSettingsFrom(ds: CustomerDisplaySettings | null | undefined): GuestNoteSettings {
  if (!ds) return NOTES_UNKNOWN;
  return {
    // Server default is on; an older payload without the flags keeps notes hidden.
    itemNotesEnabled: ds.guestItemNotesEnabled === true,
    orderNoteEnabled: ds.guestOrderNoteEnabled === true,
    presets: Array.isArray(ds.notePresets)
      ? ds.notePresets.filter((p) => typeof p === 'string' && p.trim() !== '')
      : [],
    itemNoteMaxLength:
      ds.itemNoteMaxLength && ds.itemNoteMaxLength > 0 ? ds.itemNoteMaxLength : DEFAULT_ITEM_NOTE_MAX,
    orderNoteMaxLength:
      ds.orderNoteMaxLength && ds.orderNoteMaxLength > 0 ? ds.orderNoteMaxLength : DEFAULT_ORDER_NOTE_MAX,
  };
}

/**
 * Reads the outlet's note settings from the restaurant payload (cached / de-duplicated by
 * restaurantService.getRestaurant, so every sheet on a page shares one request).
 */
export function useGuestNoteSettings(appName: string | undefined, enabled = true): GuestNoteSettings {
  const [settings, setSettings] = useState<GuestNoteSettings>(NOTES_UNKNOWN);

  useEffect(() => {
    if (!appName || !enabled) return undefined;
    let cancelled = false;
    restaurantService
      .getRestaurant(appName)
      .then((res) => {
        if (!cancelled && res?.success) {
          setSettings(noteSettingsFrom(res.displaySettings));
        }
      })
      .catch(() => {
        /* notes stay hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [appName, enabled]);

  return settings;
}
